import AuthApi from '@apis/auth/AuthApi';
import { ACCESS_TOKEN_MAX_AGE_SECOND, REFRESH_TOKEN_MAX_AGE_SECOND } from '@constants/auth';
import colors from '@constants/colors';
import { numberRegexp, phoneFormat, phoneRegExp } from '@constants/regexp';
import useIdentification from '@hooks/auth/useIdentification';
import useIsEmailSignIn from '@hooks/auth/useIsEmailSignIn';
import useUser from '@hooks/auth/useUser';
import useLoading from '@hooks/ui/useLoading';
import useToast from '@hooks/ui/useToast';
import useProjectId from '@hooks/useProjectId';
import { NotVerificationSignInErrorSchema } from '@models/auth/schema';
import { ErrorResponse } from '@models/error';
import { type TypeActivities, useFlow } from '@router/stackflow';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
    ChangeEvent, useContext, useMemo, useState,
} from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';

import useSendCode from './useSendCode';
import useVerification from './useVerification';
import SignInContext from '../context/SignInContext';

const ENABLED_COLOR = 'var(--color-blue)';
const SUCCESS_COLOR = 'var(--color-green)';

interface IFlow {
  activityName: keyof TypeActivities,
  activityParams: object,
}

function useLogin() {
    const { t } = useTranslation('signIn');
    const { pop, push } = useFlow();
    const { useVerified, useInputValue } = useContext(SignInContext);
    const [value, setValue] = useInputValue;
    const [smsAuthNo, setSmsAuthNo] = useState<string>('');
    const [isVerified] = useVerified;
    const projectId = useProjectId();
    const [cookies, setCookie, removeCookies] = useCookies(['accessToken', 'refreshToken', 'authFlow'], {
        doNotParse: true,
    });
    const { getUserProfile } = useUser();
    const isEmail = useIsEmailSignIn();
    const onChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
        if (isEmail) {
            setValue(e.target.value);
        } else {
            setValue(e.target.value.replace(numberRegexp, '')
                .replace(phoneRegExp, phoneFormat));
        }
    };
    const onChangeCode = (e: ChangeEvent<HTMLInputElement>) => {
        setSmsAuthNo(e.target.value.replace(numberRegexp, ''));
    };
    const toast = useToast();
    const [smSend, setSmsSend] = useState<boolean>(false);
    const requestSms = useSendCode({ value, onSend: () => setSmsSend(true) });
    const requestVerify = useVerification({ authNo: smsAuthNo });
    const { startLoading, stopLoading } = useLoading();
    const handleIdentification = useIdentification();
    const { mutate } = useMutation({
        mutationFn: () => {
            if (isEmail) {
                return AuthApi.signIWithEmail({
                    email: value, projectId,
                });
            }
            return AuthApi.signInWithPhone({
                phoneNo: value.replace(/-/gi, ''),
                projectId,
            });
        },
        onMutate: startLoading,
        onSuccess(data) {
            setCookie('accessToken', data.token, {
                path: '/',
                maxAge: ACCESS_TOKEN_MAX_AGE_SECOND,
            });
            setCookie('refreshToken', data.refreshToken, {
                path: '/',
                maxAge: REFRESH_TOKEN_MAX_AGE_SECOND,
            });
            getUserProfile();
            window.localStorage.setItem('lastLoginMethod', 'PHONE');
            try {
                const parsed: IFlow[] = JSON.parse(cookies.authFlow);
                pop(200);
                for (let i = 0; i < parsed.length; i += 1) {
                    const flow = parsed[i];
                    push(flow.activityName, flow.activityParams, { animate: i + 1 === parsed.length });
                }
                removeCookies('authFlow');
            } catch (e) {
                console.error(e);
                pop(200);
            }
        },
        onError(e: AxiosError<ErrorResponse>) {
            const parsed = NotVerificationSignInErrorSchema.safeParse(e.response?.data);
            if (parsed.success) {
                toast.error('본인인증이 필요합니다.');
                handleIdentification({
                    verificationToken: parsed.data.data.verificationToken,
                    projectId,
                    marketingAgreed: false,
                    locationAgreed: false,
                });
                /*
                push('SignUpActivity', {
                    verificationToken: parsed.data.data.verificationToken,
                });
                 */
            } else {
                console.error(e);
                push('SignUpActivity', {});
                toast.error(t('toast.notMember'));
            }
        },
        onSettled: stopLoading,
    });

    const login = () => {
        if (isVerified) {
            mutate();
        } else {
            toast.error(t('toast.completeVerification'));
        }
    };
    const requestColor = smSend ? SUCCESS_COLOR : ENABLED_COLOR;
    const verityColor = useMemo<string>(() => {
        if (smSend) {
            return ENABLED_COLOR;
        }
        if (isVerified) {
            return ENABLED_COLOR;
        }
        return colors.gray5;
    }, [isVerified, smSend]);

    return {
        phoneNo: value,
        onChangeValue,
        smsAuthNo,
        onChangeCode,
        requestSms,
        requestVerify,
        login,
        requestColor,
        verityColor,
        isVerified,
    };
}

export default useLogin;
